<template>
  <div class="local-body">
    <CosmicBackground />

    <!-- BACK -->
    <div class="back-btn">
      <v-btn @click="$emit('back')" color="white" icon x-large>
        <v-icon color="white" style="font-size: 48px">mdi-chevron-left</v-icon>
      </v-btn>
    </div>

    <section class="responsive-center">
      <h1 class="title-cosmic mb-6">
        <span class="title-accent">{{ $t("brand.name") }}</span>
        <span>{{ $t("onboarding.metaTitleSuffix") }}</span>
      </h1>

      <!-- 뽑은 동물 미리보기 -->
      <div v-if="selectedModelIndex" class="drawn-pet mb-5">
        <div class="drawn-pet__frame">
          <img class="drawn-pet__img" :src="thumbnailSrc" :alt="animalName" @error="onThumbError" />
        </div>
        <div class="drawn-pet__name">{{ animalName }}</div>
      </div>

      <v-theme-provider dark>
        <div class="meta-form pa-6">
          <!-- 이름 -->
          <v-text-field
            :value="value.name"
            @input="update('name', $event)"
            :rules="[rules.required]"
            :error="nameStatus === 'taken'"
            :error-messages="nameStatus === 'taken' ? '이미 사용 중인 이름이에요' : []"
            :hint="nameStatus === 'available' ? '사용 가능한 이름이에요 ✨' : ''"
            persistent-hint
            :label="$t('onboarding.name')"
            counter="12"
            maxlength="12"
            outlined
            dense
            color="#c9b8ff"
          >
            <template #prepend><v-icon :color="nameDotColor" small>{{ nameDotIcon }}</v-icon></template>
          </v-text-field>

          <!-- 성별 -->
          <v-select
            :value="value.gender"
            @change="update('gender', $event)"
            :rules="[rules.required]"
            :items="GENDERS"
            :label="$t('onboarding.gender')"
            outlined
            dense
            color="#c9b8ff"
            item-color="#c9b8ff"
          >
            <template #prepend><v-icon :color="dotColor(filled.gender)" small>{{ dotIcon(filled.gender) }}</v-icon></template>
          </v-select>

          <!-- 나이 (0세 고정 — 갓 태어난 펫, 입력 불가) -->
          <v-text-field
            value="0세"
            :label="$t('onboarding.age')"
            disabled
            outlined
            dense
            hide-details
            color="#c9b8ff"
          >
            <template #prepend><v-icon :color="dotColor(true)" small>{{ dotIcon(true) }}</v-icon></template>
          </v-text-field>

          <!-- MBTI -->
          <v-select
            :value="value.mbti"
            @change="update('mbti', $event)"
            :rules="[rules.required]"
            :items="MBTIS"
            label="MBTI"
            outlined
            dense
            class="mt-6"
            color="#c9b8ff"
            item-color="#c9b8ff"
          >
            <template #prepend><v-icon :color="dotColor(filled.mbti)" small>{{ dotIcon(filled.mbti) }}</v-icon></template>
          </v-select>

          <!-- 직업 -->
          <v-select
            :value="value.job"
            @change="update('job', $event)"
            :rules="[rules.required]"
            :items="JOBS"
            :label="$t('onboarding.job')"
            outlined
            dense
            color="#c9b8ff"
            item-color="#c9b8ff"
          >
            <template #prepend><v-icon :color="dotColor(filled.job)" small>{{ dotIcon(filled.job) }}</v-icon></template>
          </v-select>

          <!-- 행성 -->
          <v-select
            :value="value.planet"
            @change="update('planet', $event)"
            :rules="[rules.required]"
            :items="PLANETS"
            :label="$t('onboarding.planet')"
            outlined
            dense
            color="#c9b8ff"
            item-color="#c9b8ff"
          >
            <template #prepend><v-icon :color="dotColor(filled.planet)" small>{{ dotIcon(filled.planet) }}</v-icon></template>
          </v-select>
        </div>
      </v-theme-provider>

      <!-- 시작하기 (전단계 뽑기 버튼과 동일 사이즈) -->
      <div class="d-flex justify-center mt-4">
        <v-btn
          class="cosmic-cta gacha-btn"
          rounded
          :loading="isLoading"
          :disabled="!isComplete || nameStatus === 'taken' || nameStatus === 'checking'"
          @click="onComplete"
        >
          <span>{{ $t("onboarding.start") }}</span>
        </v-btn>
      </div>
    </section>
  </div>
</template>

<script>
import { MBTIS, GENDERS, JOBS, PLANETS, isMetaShape, isMetaComplete, pickRandom } from "./onboarding-meta";
import CosmicBackground from "../../components/CosmicBackground.vue";
import SupabaseApi from "@/api/SupabaseApi";

export default {
  name: "OnboardingMeta",
  components: { CosmicBackground },
  // CONTROLLED: 내부 폼 상태를 갖지 않는다. value(부모 소유)를 반영하고 변경은 input 으로 올린다.
  props: {
    // VirtupetMeta — 부모가 소유/주입하는 상태
    value: {
      type: Object,
      required: true,
      validator: isMetaShape,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    // STEP1에서 뽑은 동물 (modelIndex). 정보 입력 중 미리보기 표시용.
    selectedModelIndex: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      MBTIS,
      GENDERS,
      JOBS,
      PLANETS,
      rules: {
        required: (v) => (!!v && String(v).trim().length > 0) || this.$t("onboarding.required"),
      },
      // 이름 중복 체크 상태: idle | checking | available | taken | error
      nameStatus: "idle",
    };
  },
  created() {
    // 페이지 진입 시 MBTI·직업·행성은 랜덤 기본값으로 채운다(비어있을 때만 — 뒤로/앞으로 시 재추첨 방지).
    const patch = {};
    if (!this.value.mbti) patch.mbti = pickRandom(MBTIS);
    if (!this.value.job) patch.job = pickRandom(JOBS);
    if (!this.value.planet) patch.planet = pickRandom(PLANETS);
    if (Object.keys(patch).length) this.$emit("input", { ...this.value, ...patch });
    this._scheduleNameCheck(this.value.name); // 복원된 이름이 있으면 즉시 체크
  },
  beforeDestroy() {
    clearTimeout(this._nameTimer);
  },
  watch: {
    "value.name"(n) {
      this._scheduleNameCheck(n);
    },
  },
  computed: {
    // 메타 전부 필수 충족해야 완료 가능 (약관 동의는 /user/new에서 처리)
    isComplete() {
      return isMetaComplete(this.value);
    },
    // 각 필드 채움 여부 (왼쪽 valid 표시용)
    filled() {
      const v = this.value;
      return {
        name: !!(v.name && String(v.name).trim().length),
        gender: !!v.gender,
        mbti: !!v.mbti,
        job: !!v.job,
        planet: !!v.planet,
      };
    },
    // 뽑은 동물 썸네일 (public/trial/thumbnails — PetSelectPage/PreviewModelCard와 동일 규칙)
    thumbnailSrc() {
      return `/trial/thumbnails/${this.selectedModelIndex}_thumbnail.png`;
    },
    animalName() {
      return this.selectedModelIndex ? this.$t("animals." + this.selectedModelIndex) : "";
    },
    // 이름 필드 좌측 상태 아이콘 (중복 체크 반영)
    nameDotIcon() {
      if (this.nameStatus === "available") return "mdi-check-circle";
      if (this.nameStatus === "taken") return "mdi-alert-circle";
      if (this.nameStatus === "checking") return "mdi-dots-horizontal-circle-outline";
      return "mdi-circle-outline";
    },
    nameDotColor() {
      if (this.nameStatus === "available") return "#7CFFA0";
      if (this.nameStatus === "taken") return "#FF6F8E";
      return "rgba(201, 184, 255, 0.45)";
    },
  },
  methods: {
    onThumbError(e) {
      e.target.src = "/trial/thumbnails/bear_thumbnail.png"; // 폴백 (PetSelectPage와 동일)
    },
    // 이름 중복 체크 (디바운스 400ms, 글로벌 유일/대소문자무시는 RPC가 처리). 최신 요청만 반영.
    _scheduleNameCheck(raw) {
      const name = (raw || "").trim();
      clearTimeout(this._nameTimer);
      if (!name) {
        this.nameStatus = "idle";
        return;
      }
      this.nameStatus = "checking";
      const token = (this._nameToken = (this._nameToken || 0) + 1);
      this._nameTimer = setTimeout(async () => {
        try {
          const ok = await SupabaseApi.PetProfile.isNameAvailable(name);
          if (token !== this._nameToken) return; // 더 최신 입력이 있으면 무시
          this.nameStatus = ok ? "available" : "taken";
        } catch (e) {
          if (token !== this._nameToken) return;
          this.nameStatus = "error"; // 네트워크 등 — 제출 시 23505로 최종 방어
          console.warn("[OnboardingMeta] 이름 중복 체크 실패", e);
        }
      }, 400);
    },
    // 입력 박스 왼쪽 valid 상태 표시
    dotIcon(ok) {
      return ok ? "mdi-check-circle" : "mdi-circle-outline";
    },
    dotColor(ok) {
      return ok ? "#7CFFA0" : "rgba(201, 184, 255, 0.45)";
    },
    // 단일 진입점: 변경분만 합쳐 새 객체로 emit → 부모가 상태를 갱신/보관
    update(key, val) {
      this.$emit("input", { ...this.value, [key]: val });
    },
    onComplete() {
      if (this.isComplete) this.$emit("complete");
    },
  },
};
</script>

<style scoped>
.local-body {
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: auto;
  background-color: #0c0822;
}

.back-btn {
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 3;
}

.responsive-center {
  position: relative;
  z-index: 1;
  width: 90%;
  max-width: 520px;
  margin: auto;
  padding-top: 72px;
  padding-bottom: 40px;
}

.title-cosmic {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-weight: 400;
  font-size: clamp(30px, 5vw, 44px);
  color: #f3effc;
  text-shadow: 0 0 18px rgba(124, 92, 255, 0.45);
  user-select: none; /* 제목 드래그 선택 방지 */
}
.title-cosmic .title-accent {
  color: #d9b3ff;
  /* 글로우를 옅게 — 진한 글로우가 선택 하이라이트처럼 보이던 문제 완화 */
  text-shadow: 0 0 10px rgba(217, 179, 255, 0.35);
}

/* 뽑은 동물 미리보기 */
.drawn-pet {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.drawn-pet__frame {
  width: 128px;
  height: 128px;
  border-radius: 20px;
  padding: 6px;
  background: rgba(22, 16, 45, 0.62);
  border: 1px solid rgba(149, 85, 253, 0.34);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45), 0 0 28px rgba(124, 92, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
}
.drawn-pet__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.drawn-pet__name {
  margin-top: 10px;
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif;
  font-size: 20px;
  color: #f3effc;
  text-shadow: 0 0 14px rgba(124, 92, 255, 0.5);
}

/* 완전 다크 글래스 폼 패널 */
.meta-form {
  background: rgba(22, 16, 45, 0.62);
  border-radius: 16px;
  border: 1px solid rgba(149, 85, 253, 0.34);
  backdrop-filter: blur(10px);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.5), 0 0 36px rgba(124, 92, 255, 0.22),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;
}
/* 다크 입력 가독성 보정 */
.meta-form >>> .v-input input,
.meta-form >>> .v-select__selection {
  color: #f3effc;
}
.meta-form >>> .v-label {
  color: #b9aee0;
}
.meta-form >>> .theme--dark.v-text-field--outlined fieldset {
  border-color: rgba(201, 184, 255, 0.32);
}
/* 좌측 valid 상태 아이콘 위치 보정 (아래 5px, 우측 1px) */
.meta-form >>> .v-input__prepend-outer {
  transform: translate(1px, 5px);
}

/* 공용 CTA — 404 버튼과 동일 룩 */
.cosmic-cta {
  font-family: "Ownglyph_meetme-Rg", "maplestory", sans-serif !important;
  font-size: 21px !important;
  color: #fff !important;
  background: linear-gradient(135deg, #7c5cff 0%, #b15bff 45%, #ff6fae 100%) !important;
  box-shadow: 0 10px 28px rgba(124, 92, 255, 0.5) !important;
  letter-spacing: 0.5px;
}

/* 가챠 버튼 공통 사이즈(전단계 뽑기/다시뽑기와 통일) */
.gacha-btn {
  min-width: 280px;
  height: 66px !important;
  font-size: 23px !important;
  padding: 0 32px !important;
}
</style>
